import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ConditionalFilterPlugin,
  FetchOptionsFunction,
  FileSelection,
  jsonEditorWidgets,
} from '@asset-metrix/portal-ui';
import {
  JsonError,
  JsonSchema,
  useEditor,
  useEditorPlugin,
  Widget,
} from '@sagold/react-json-editor';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';

import { fetchDocUploadModalValues } from '../../api/portalGateway/docviewUploadFormValues';

import { useDocUploadStore } from './store/store';
import { computeFileInitialData } from './computeFileInitialData';
import { METADATA_EXTRACTION_TYPE } from './constants';
import { useMetadataInputsDisabled } from './hooks';

export const MetaDataInputField = ({
  editorInitialData,
  extractionMethod,
  file,
  jsonSchema,
  onEditorDataChange,
  preFilter,
  setErrors,
}: {
  editorInitialData: Record<string, unknown>;
  extractionMethod: METADATA_EXTRACTION_TYPE;
  file: FileSelection;
  jsonSchema: JsonSchema;
  onEditorDataChange: (fileId: string, data: Record<string, unknown>) => void;
  preFilter: Record<string, string | string[] | null>;
  setErrors: (fileId: string, error: JsonError[]) => void;
}) => {
  const {
    tableModalValues,
    contentId,
    fileNameMetadataExtractionConfig,
    isTableModalValuesLoading,
    perFileMetaData,
    setModalError,
  } = useDocUploadStore(
    useShallow((state) => ({
      tableModalValues: state.tableModalValues,
      contentId: state.contentId,
      fileNameMetadataExtractionConfig: state.fileNameMetadataExtractionConfig,
      isTableModalValuesLoading: state.isTableModalValuesLoading,
      perFileMetaData: state.perFileMetaData,
      setModalError: state.setModalError,
    }))
  );
  const { t } = useTranslation();

  const fileMetadata = perFileMetaData[file.id];
  const metadataInputsDisabled = useMetadataInputsDisabled();

  const [initialData] = useState(() =>
    computeFileInitialData({
      tableModalValues,
      fileNameMetadataExtractionConfig,
      editorInitialData,
      extractionMethod,
      file,
      fileMetadata,
      jsonSchema,
    })
  );

  const queryClient = useQueryClient();

  const fetchOptions = useCallback<FetchOptionsFunction>(
    async (filter) => {
      try {
        const result = await queryClient.fetchQuery({
          queryKey: [
            'tableModalValues',
            {
              contentId,
              filter: {
                ...preFilter,
                ...filter,
              },
            },
          ],
          queryFn: fetchDocUploadModalValues,
        });
        return result || {};
      } catch (error) {
        setModalError(t('app.upload.conditionalFilterOptionsError'));
        throw error;
      }
    },
    [queryClient, contentId, preFilter, setModalError, t]
  );

  const isFetchingMetadata = file.uploadState === 'isFetchingMetadata';
  const isFieldLoading =
    isFetchingMetadata || Boolean(isTableModalValuesLoading);
  const editor = useEditor({
    widgets: jsonEditorWidgets,
    schema: jsonSchema,
    onChange: (_data, _root, editor) => {
      setErrors(file.id, editor.getErrors());
      onEditorDataChange(file.id, editor.getData());
    },
    validate: true,
    data: initialData,
  });
  useEditorPlugin(editor, ConditionalFilterPlugin, {
    fetchOptions,
    options: {
      isLoading: isFetchingMetadata,
      skipPlugin: isFetchingMetadata,
    },
  });

  return (
    <div
      className={clsx('portal-form rje-form rje-theme rje-theme--light')}
      style={{ cursor: 'default', width: '100%' }}
      onClick={(e) => e.stopPropagation()}
    >
      <Widget
        editor={editor}
        options={{
          disabled: metadataInputsDisabled,
          loading: isFieldLoading,
        }}
      />
    </div>
  );
};
